// One place for everything the storefront shares across pages: whether the store is open to this visitor, their cart,
// the Minecraft account they are shopping on, and the dialogs. It makes no store request until the store is visible,
// so a closed store costs every other page nothing. The catalog loads only when a store page asks for it.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { ApiError } from "../../api";
import { areaVisible, useArea, useSite } from "../context";
import { storeApi, type Cart, type Currency, type StoreAccount, type StoreCatalog } from "./storeSite";

export type StoreDialogName = "cart" | "account" | null;
export type CatalogState = { data?: StoreCatalog; loading: boolean; error?: ApiError };

export type StoreSession = {
  visible: boolean;
  signedIn: boolean;
  cart: Cart | null;
  count: number;
  catalog: CatalogState;
  loadCatalog: () => void;
  accounts: StoreAccount[] | null;
  selected: StoreAccount | null;
  refreshAccounts: () => Promise<void>;
  select: (uuid: string) => Promise<void>;
  add: (itemId: number, currency: Currency, quantity: number) => Promise<void>;
  setQuantity: (itemId: number, currency: Currency, quantity: number) => Promise<void>;
  remove: (itemId: number, currency: Currency, name: string) => Promise<void>;
  clear: () => Promise<void>;
  refreshCart: () => void;
  dialog: StoreDialogName;
  openCart: () => void;
  openAccount: () => void;
  closeDialog: () => void;
  message: string;
  announce: (text: string) => void;
};

const idle = async () => undefined;
const INERT: StoreSession = {
  visible: false, signedIn: false, cart: null, count: 0, catalog: { loading: false }, loadCatalog: () => undefined, accounts: null, selected: null,
  refreshAccounts: idle, select: idle, add: idle, setQuantity: idle, remove: idle, clear: idle, refreshCart: () => undefined, dialog: null, openCart: () => undefined, openAccount: () => undefined, closeDialog: () => undefined, message: "", announce: () => undefined,
};
const StoreContext = createContext<StoreSession>(INERT);
export const useStore = () => useContext(StoreContext);

// Pages that do not load the catalog (the cart) still need the list of linked accounts to offer "Change account".
export function useEnsureAccounts() {
  const { visible, signedIn, accounts, refreshAccounts } = useStore();
  useEffect(() => { if (visible && signedIn && accounts === null) refreshAccounts().catch(() => undefined); }, [visible, signedIn, accounts, refreshAccounts]);
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { me } = useSite();
  const area = useArea("public.store");
  const signedIn = Boolean(me.user);
  // The store ships switched off, so an access list that has not loaded (or failed) means closed, unlike every other page.
  const visible = area ? areaVisible(area, signedIn) : false;
  const person = visible ? (me.user?.id ?? "guest") : "off";

  const [cart, setCart] = useState<Cart | null>(null);
  const [accounts, setAccounts] = useState<StoreAccount[] | null>(null);
  const [selected, setSelected] = useState<StoreAccount | null>(null);
  const [catalog, setCatalog] = useState<CatalogState>({ loading: false });
  const [wanted, setWanted] = useState(false);
  const [cartTick, setCartTick] = useState(0);
  const [catalogTick, setCatalogTick] = useState(0);
  const [dialog, setDialog] = useState<StoreDialogName>(null);
  const [message, setMessage] = useState("");
  const returnTo = useRef<StoreDialogName>(null);
  const shown = useRef<StoreDialogName>(null);
  shown.current = dialog;
  const location = useLocation();

  const applyCart = useCallback((next: Cart) => { setCart(next); setSelected(next.account); }, []);

  useEffect(() => {
    setAccounts(null); setSelected(null); setCatalog({ loading: false });
  }, [person]);

  useEffect(() => {
    if (!visible || !signedIn) { setCart(null); return; }
    let active = true;
    storeApi.cart().then((next) => { if (active) applyCart(next); }).catch(() => undefined);
    return () => { active = false; };
  }, [visible, signedIn, person, cartTick, applyCart]);

  useEffect(() => {
    if (!visible || !wanted) return;
    let active = true;
    setCatalog((current) => ({ data: current.data, loading: true }));
    storeApi.catalog().then((data) => {
      if (!active) return;
      setCatalog({ data, loading: false });
      setAccounts(data.account.accounts);
      setSelected(data.account.selected);
    }).catch((error: ApiError) => { if (active) setCatalog((current) => ({ data: current.data, loading: false, error })); });
    return () => { active = false; };
  }, [visible, wanted, person, catalogTick]);

  // Dialogs belong to the page they were opened on.
  useEffect(() => { setDialog(null); returnTo.current = null; }, [location.pathname, location.search]);

  const loadCatalog = useCallback(() => { setWanted(true); setCatalogTick((tick) => tick + 1); }, []);
  const announce = useCallback((text: string) => setMessage(text), []);
  const openCart = useCallback(() => setDialog("cart"), []);
  // The account picker can open over the cart; closing it brings the cart back.
  const openAccount = useCallback(() => { returnTo.current = shown.current === "cart" ? "cart" : null; setDialog("account"); }, []);
  const closeDialog = useCallback(() => {
    const back = shown.current === "account" ? returnTo.current : null;
    returnTo.current = null;
    setDialog(back);
  }, []);

  // Several pieces can ask at once (the shopping bar and the cart on the cart page); they share one request.
  const accountsRequest = useRef<Promise<void> | null>(null);
  const refreshAccounts = useCallback(() => {
    accountsRequest.current ??= storeApi.accounts().then((result) => { setAccounts(result.accounts); }).finally(() => { accountsRequest.current = null; });
    return accountsRequest.current;
  }, []);
  const select = useCallback(async (uuid: string) => {
    const result = await storeApi.select(uuid);
    setSelected(result.account); setAccounts(result.accounts);
    setCartTick((tick) => tick + 1); setCatalogTick((tick) => tick + 1);
    setMessage(`Now shopping on ${result.account.name}.`);
  }, []);
  const add = useCallback(async (itemId: number, currency: Currency, quantity: number) => {
    applyCart(await storeApi.add(itemId, currency, quantity));
    setMessage("Added to your cart.");
  }, [applyCart]);
  const setQuantity = useCallback(async (itemId: number, currency: Currency, quantity: number) => {
    applyCart(await storeApi.setQuantity(itemId, currency, quantity));
    setMessage(`Quantity set to ${quantity}.`);
  }, [applyCart]);
  const remove = useCallback(async (itemId: number, currency: Currency, name: string) => {
    applyCart(await storeApi.remove(itemId, currency));
    setMessage(`${name} removed from your cart.`);
  }, [applyCart]);
  const clear = useCallback(async () => { applyCart(await storeApi.clear()); setMessage("Your cart is empty."); }, [applyCart]);
  const refreshCart = useCallback(() => setCartTick((tick) => tick + 1), []);

  const value = useMemo<StoreSession>(() => ({
    visible, signedIn, cart, count: cart?.count ?? 0, catalog, loadCatalog, accounts, selected, refreshAccounts, select, add, setQuantity, remove, clear, refreshCart,
    dialog, openCart, openAccount, closeDialog, message, announce,
  }), [visible, signedIn, cart, catalog, loadCatalog, accounts, selected, refreshAccounts, select, add, setQuantity, remove, clear, refreshCart, dialog, openCart, openAccount, closeDialog, message, announce]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
